// src/utils/formatters.js

/**
 * Format currency value for Argentine Peso (ARS)
 * @param {number} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, options = {}) => {
    const { decimals = 2, compact = false } = options;
    
    if (value === null || value === undefined || isNaN(value)) {
        return '$0.00';
    }

    const formatter = new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: compact ? 'compact' : 'standard',
        compactDisplay: 'short'
    });

    return formatter.format(value);
};

/**
 * Format large currency values with K/M suffixes for better readability
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted currency string with suffix
 */
export const formatCurrencyCompact = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '$0';
    }

    if (Math.abs(value) >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
        return `$${(value / 1000).toFixed(1)}K`;
    }
    
    return formatCurrency(value, { decimals: 0 });
};

/**
 * Format number with thousands separator (no currency symbol)
 * @param {number} value - The numeric value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0';
    }

    return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

/**
 * Format percentage value
 * @param {number} value - The numeric value to format (e.g., 0.25 for 25%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @param {boolean} isDecimal - Whether input is decimal (0.25) or percentage (25)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1, isDecimal = true) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0%';
    }

    const percentValue = isDecimal ? value * 100 : value;
    return `${percentValue.toFixed(decimals)}%`;
};

/**
 * Format date for display in Argentina format (DD/MM/YYYY)
 * @param {string|Date} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
    if (!date) return '-';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
        return '-';
    }

    const defaultOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...options
    };

    return new Intl.DateFormat('es-AR', defaultOptions).format(dateObj);
};

/**
 * Format date with time in Argentina format
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
    if (!date) return '-';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
        return '-';
    }

    return new Intl.DateFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(dateObj);
};

/**
 * Calculate and format profit margin percentage
 * @param {number} profit - Profit amount
 * @param {number} revenue - Revenue amount
 * @returns {string} Formatted margin percentage
 */
export const formatProfitMargin = (profit, revenue) => {
    if (!revenue || revenue === 0) {
        return '0%';
    }
    
    const margin = (profit / revenue) * 100;
    return formatPercentage(margin, 1, false);
};

/**
 * Format invoice type for display
 * @param {string} type - Invoice type code
 * @returns {string} Formatted invoice type
 */
export const formatInvoiceType = (type) => {
    const typeMap = {
        'FACTURA A': 'Factura A',
        'FACTURA B': 'Factura B',
        'FACTURA C': 'Factura C',
        'PRESUPUESTO': 'Presupuesto',
        'NOTA DE CREDITO A': 'NC A',
        'NOTA DE CREDITO B': 'NC B',
        'NOTA DE CREDITO C': 'NC C'
    };
    
    return typeMap[type?.toUpperCase()] || type || '-';
};
